import React from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const WAVE_SPLITS_QUERY = graphql`
  query WaveSplitsGraphQuery(
    $eventId: ID!
    $roundNum: Int!
    $eventCourseId: ID!
  ) {
    pgaWaveSplitsForRound(
      eventId: $eventId
      roundNum: $roundNum
      eventCourseId: $eventCourseId
    ) {
      roundAvg
      amAvg
      pmAvg
      minToPar
      maxToPar
      amScores {
        score
        num
      }
      pmScores {
        score
        num
      }
    }
  }
`;

class CustomizedLabel extends React.Component {
  render() {
    const { x, y, fill, value, width, pm } = this.props;
    if (value === 0 || (pm && value > 0)) {
      return null;
    }

    return (
      <text
        x={x + width / 2}
        y={y}
        dy={pm ? 16 : -6}
        fontSize="16"
        fontFamily="sans-serif"
        fill={fill}
        textAnchor="middle"
      >
        {pm ? value * -1 : value}
      </text>
    );
  }
}

export default function WaveSplitsGraph({ eventId, roundNum, eventCourseId }) {
  const data = useLazyLoadQuery(WAVE_SPLITS_QUERY, {
    eventId: eventId,
    roundNum: roundNum,
    eventCourseId: eventCourseId,
  });

  const minScore = data.pgaWaveSplitsForRound.minToPar;
  const maxScore = data.pgaWaveSplitsForRound.maxToPar;

  const roundAvg = data.pgaWaveSplitsForRound.roundAvg;
  const amAvg = data.pgaWaveSplitsForRound.amAvg;
  const pmAvg = data.pgaWaveSplitsForRound.pmAvg;

  const displayData = React.useMemo(() => {
    const compiledDict = {};

    const amWave = data.pgaWaveSplitsForRound.amScores;
    const pmWave = data.pgaWaveSplitsForRound.pmScores;

    let amWaveAllEven = true;
    let pmWaveAllEven = true;

    amWave.forEach((entry) => {
      if (!(entry.score in compiledDict)) {
        compiledDict[entry.score] = {
          am: 0,
          pm: 0,
        };
      }

      if (entry.score !== 0) {
        amWaveAllEven = false;
      }

      compiledDict[entry.score]["am"] = entry.num;
    });

    pmWave.forEach((entry) => {
      if (!(entry.score in compiledDict)) {
        compiledDict[entry.score] = {
          am: 0,
          pm: 0,
        };
      }

      if (entry.score !== 0) {
        pmWaveAllEven = false;
      }

      compiledDict[entry.score]["pm"] = entry.num * -1;
    });

    if (amWaveAllEven && 0 in compiledDict) {
      compiledDict[0]["am"] = 0;
    }

    if (pmWaveAllEven && 0 in compiledDict) {
      compiledDict[0]["pm"] = 0;
    }

    return Object.entries(compiledDict).map(([score, values]) => {
      return {
        score: score,
        amNum: values["am"],
        pmNum: values["pm"],
      };
    });
  }, [eventId]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={displayData}
        stackOffset="sign"
        margin={{
          top: 25,
          right: 0,
          left: 0,
          bottom: 25,
        }}
      >
        <YAxis domain={["dataMin - 5", "dataMax + 5"]} />
        <XAxis
          dataKey="score"
          domain={[minScore, maxScore]}
          ticks={[...Array(maxScore - minScore + 3).keys()].map((i) => {
            if (minScore < 0) {
              return i + minScore - 1;
            } else {
              return i - minScore;
            }
          })}
          type="number"
        />
        {/* Round Avg Text */}
        <text
          x="18%"
          y="17%"
          dy={+0}
          style={{ fontSize: 20, fontWeight: "bold", fill: "gray" }}
          width={200}
          textAnchor="middle"
        >
          Round {roundNum}
        </text>
        <text
          x="18%"
          y="17%"
          dy={+20}
          style={{
            fontSize: 20,
            fontWeight: "bold",
            fill: "var(--gup-green)",
          }}
          width={200}
          textAnchor="middle"
        >
          {roundAvg}
        </text>

        {/* AM Wave Avg Text */}
        {amAvg !== null ? (
          <>
            <text
              x="90%"
              y="17%"
              dy={+0}
              style={{ fontSize: 20, fontWeight: "bold", fill: "gray" }}
              width={200}
              textAnchor="middle"
            >
              AM Wave
            </text>
            <text
              x="90%"
              y="17%"
              dy={+20}
              style={{
                fontSize: 24,
                fontWeight: "bold",
                fill: "var(--gup-green)",
              }}
              width={200}
              textAnchor="middle"
            >
              {amAvg}
            </text>
          </>
        ) : null}

        {/* PM Wave Avg Text */}
        {pmAvg !== null ? (
          <>
            <text
              x="90%"
              y="77%"
              dy={+0}
              style={{ fontSize: 20, fontWeight: "bold", fill: "gray" }}
              width={200}
              textAnchor="middle"
            >
              PM Wave
            </text>
            <text
              x="90%"
              y="77%"
              dy={-20}
              style={{
                fontSize: 20,
                fontWeight: "bold",
                fill: "var(--gup-green)",
              }}
              width={200}
              textAnchor="middle"
            >
              {pmAvg}
            </text>
          </>
        ) : null}
        <ReferenceLine y={0} stroke="#000" />
        <Bar
          dataKey="amNum"
          stackId="a"
          fill="blue"
          label={<CustomizedLabel pm={false} />}
        />
        {pmAvg ? (
          <Bar
            dataKey="pmNum"
            stackId="a"
            fill="red"
            label={<CustomizedLabel pm={true} />}
          />
        ) : null}
      </BarChart>
    </ResponsiveContainer>
  );
}
